import * as Cart from './storefrontCart';
import { loadSession } from './storefrontSession';
import StorefrontAPI from './storefrontApi';

/**
 * Keeping the browser's basket and the server's in step.
 *
 * The browser's cart stays exactly where it is. It has to: someone shopping
 * without an account has nowhere else to put a basket, and asking them to
 * sign in before they can add anything is the fastest way to lose the sale.
 *
 * What was missing is what happens once they do sign in. A basket filled on a
 * phone was not there on a laptop, and clearing site data threw away a
 * considered order — because localStorage was the only copy there was.
 *
 * So: the browser's cart remains the one the pages read and write, and every
 * change is mirrored to the server for a customer who is signed in. The
 * server's copy is read back at sign-in and on a fresh device. Nothing here
 * blocks the shopper — a failed sync leaves the local cart working, which is
 * the one they can see.
 */

const signedIn = (brandId) => Boolean(loadSession(brandId)?.token);

/**
 * Mirror every local change up, for whoever is signed in.
 *
 * Subscribing once here rather than calling a push at each of the half-dozen
 * places that change a cart: one of those would be forgotten, and the symptom
 * — a basket that is right on this device and stale on the next — is the kind
 * nobody reports because it looks like their own mistake.
 */
Cart.subscribe(({ brandId, items }) => { pushCart(brandId, items); });

/**
 * Send the local cart up, replacing what is stored.
 *
 * Fire-and-forget on purpose. This runs behind ordinary cart interactions and
 * must never make "add to basket" feel slow, or fail one.
 */
export function pushCart(brandId, items) {
  if (!brandId || !signedIn(brandId)) return Promise.resolve();
  return StorefrontAPI.cartSet(brandId, items || Cart.loadCart(brandId)).catch(() => {});
}

/**
 * Fold the local basket into the stored one, then adopt the result.
 *
 * Called at sign-in. Neither side wins outright — both are things this person
 * chose, and quietly dropping either is only discovered at checkout. The
 * server adds the quantities for anything in both; what comes back is what
 * the browser then holds.
 */
export async function mergeOnSignIn(brandId) {
  if (!brandId) return null;
  const local = Cart.loadCart(brandId);
  try {
    const stored = await StorefrontAPI.cartMerge(brandId, local);
    const items = Array.isArray(stored?.items) ? stored.items : [];
    Cart.replaceCart(brandId, items.map(fromServer));
    return items;
  } catch (_) {
    // Signing in must not cost somebody their basket, so the local one stands.
    return null;
  }
}

/**
 * Adopt the stored cart on a device that has none.
 *
 * Only when the local basket is empty. A shopper with something in front of
 * them must not have it replaced by a cart from another device — that is the
 * version of this that loses a sale rather than saving one.
 */
export async function adoptStoredCart(brandId) {
  if (!brandId || !signedIn(brandId)) return null;
  if (Cart.loadCart(brandId).length > 0) return null;
  try {
    const stored = await StorefrontAPI.cartGet(brandId);
    const items = Array.isArray(stored?.items) ? stored.items : [];
    if (items.length === 0) return null;
    Cart.replaceCart(brandId, items.map(fromServer));
    return items;
  } catch (_) {
    return null;
  }
}

/** Empty both, e.g. once an order is placed. */
export function clearBoth(brandId) {
  Cart.clearCart(brandId);
  if (signedIn(brandId)) StorefrontAPI.cartClear(brandId).catch(() => {});
}

/**
 * The server stores ids as ObjectIds and the browser compares them as
 * strings, so a line coming back has to be flattened or every comparison in
 * the cart page quietly fails.
 */
const fromServer = (line) => ({
  product_id: String(line.product_id || ''),
  variant_id: String(line.variant_id || ''),
  sku: line.sku || '',
  name: line.name || '',
  variant_name: line.variant_name || '',
  unit_price: Number(line.unit_price) || 0,
  currency: line.currency || 'USD',
  image_url: line.image_url || '',
  quantity: Number(line.quantity) || 1,
});
